import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Kbd,
  ScrollArea,
  Tabs,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconDownload,
  IconRefresh,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { ImportWizardState } from '../types';
import { getStudyForms } from '@/services/studyFormsApi';
import JsonView from '@uiw/react-json-view';
import { shouldShowDebugFeatures } from '@/utils/debugUtils';

interface StudyDataImportDebugPanelProps {
  wizardState: ImportWizardState;
}

/**
 * Study Data Import Debug Panel - debug tools for study data import section
 * Opens/closes with 'Z' key, slides in from the right
 * Shows study forms structure and current mapping object with upload/download functionality
 */
export const StudyDataImportDebugPanel: React.FC<
  StudyDataImportDebugPanelProps
> = ({ wizardState }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('study-forms');
  const [studyForms, setStudyForms] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const shouldShow = shouldShowDebugFeatures();

  // Keyboard handler for 'Z' key to toggle panel
  useEffect(() => {
    // Debug panels must be inert when debug features are disabled.
    if (!shouldShow) return;

    function handleKeydown(event: KeyboardEvent) {
      const isModifierActive =
        event.altKey || event.ctrlKey || event.shiftKey || event.metaKey;
      const isKeyboardInputActive = document.activeElement?.closest(`
        [contenteditable],
        [role="combobox"],
        [role="listbox"],
        [role="menu"],
        input:not([type="radio"], [type="checkbox"]),
        select,
        textarea
      `);
      const isKeyZ = event.key?.toUpperCase() === 'Z' && !isModifierActive;

      if (isKeyZ && !isKeyboardInputActive) {
        setOpen(prev => !prev);
      }
    }

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [shouldShow]);

  // Load study forms data
  const loadStudyForms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getStudyForms({ per_page: 10000 });
      setStudyForms(response.data);
    } catch {
      setStudyForms({ error: 'Failed to load study forms' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Download current mapping as JSON file
  const downloadMapping = useCallback(() => {
    const mappingData = {
      wizardState: wizardState,
      timestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(mappingData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `study-data-import-debug-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [wizardState]);

  // Upload mapping from JSON file
  const uploadMapping = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = event => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const data = JSON.parse(e.target?.result as string);
            if (data.studyDataImport && data.mapping) {
              // Restore the state from the uploaded file
              // You might want to add specific store methods to restore state
              alert(
                'Mapping loaded from file. Refresh the page to apply changes.'
              );
            } else {
              alert('Invalid mapping file format');
            }
          } catch {
            alert('Failed to parse JSON file');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, []);

  // Only show when debug features are enabled
  if (!shouldShow) {
    return null;
  }

  // Hidden trigger button with keyboard hint
  if (!open) {
    return (
      <VisuallyHidden>
        <Box
          position="fixed"
          bottom="4"
          right="4"
          style={{
            zIndex: 9998, // Slightly lower than DevToolsPanel
          }}
        >
          <Tooltip
            content="Press Z to show/hide Study Data Import Debug Panel"
            side="bottom"
            sideOffset={6}
          >
            <Kbd
              asChild
              size="medium"
              tabIndex={0}
              className="rt-ThemePanelShortcut"
            >
              <button
                onClick={() => setOpen(true)}
                style={{
                  background: 'var(--color-panel-solid)',
                  border: '1px solid var(--orange-6)',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: 'var(--orange-11)',
                }}
              >
                Study Debug
              </button>
            </Kbd>
          </Tooltip>
        </Box>
      </VisuallyHidden>
    );
  }

  return (
    <Box
      position="fixed"
      top="0"
      right="0"
      mr="4"
      mt="4"
      style={{
        zIndex: 9998,
        overflow: 'hidden',
        maxHeight: 'calc(100vh - var(--space-4) - var(--space-4))',
        borderRadius: 'var(--radius-4)',
        backgroundColor: 'var(--color-panel-solid)',
        transformOrigin: 'top center',
        transitionProperty: 'transform, box-shadow',
        transitionDuration: '200ms',
        transitionTimingFunction: open ? 'ease-out' : 'ease-in',
        transform: open ? 'none' : 'translateX(105%)',
        boxShadow: open ? 'var(--shadow-5)' : 'var(--shadow-2)',
        border: '1px solid var(--orange-6)',
        minWidth: '600px',
        maxWidth: '90vw',
        height: 'calc(100vh - var(--space-4) - var(--space-4))',
      }}
    >
      <ScrollArea style={{ height: '100%' }}>
        <Box flexGrow="1" position="relative">
          {/* Close button */}
          <Box
            position="absolute"
            top="0"
            right="0"
            m="2"
            style={{ zIndex: 1000 }}
          >
            <Tooltip
              content="Press Z to hide Study Data Import Debug Panel"
              side="bottom"
              sideOffset={6}
            >
              <Kbd
                asChild
                size="small"
                tabIndex={0}
                className="rt-ThemePanelShortcut"
              >
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: 'var(--color-panel-solid)',
                    border: '1px solid var(--orange-6)',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconX size={14} />
                </button>
              </Kbd>
            </Tooltip>
          </Box>

          <Box
            style={{
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-5)',
            }}
          >
            <Flex align="center" justify="between">
              <Heading size="h6">Study Data Import Debug</Heading>
              <Badge color="orange" variant="soft">
                Development Only
              </Badge>
            </Flex>
            <Text size="2" color="gray">
              Press <Kbd size="small">Z</Kbd> to toggle this panel
            </Text>
          </Box>

          <Box
            style={{
              padding: '0 var(--space-5) var(--space-5) var(--space-5)',
            }}
          >
            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Trigger value="study-forms">Study Forms</Tabs.Trigger>
                <Tabs.Trigger value="mapping">Current Mapping</Tabs.Trigger>
                <Tabs.Trigger value="import-state">Import State</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="study-forms" style={{ marginTop: '1rem' }}>
                <Card>
                  <Flex direction="column" gap="3">
                    <Flex justify="between" align="center">
                      <Text size="3" weight="bold">
                        Study Forms Structure (/v1/study_forms)
                      </Text>
                      <Button
                        size="small"
                        variant="soft"
                        onClick={loadStudyForms}
                        disabled={loading}
                      >
                        <IconRefresh size={14} />
                        {loading ? 'Loading...' : 'Refresh'}
                      </Button>
                    </Flex>

                    <Box
                      style={{
                        background: 'var(--gray-2)',
                        padding: '1rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: '500px',
                      }}
                    >
                      {studyForms ? (
                        <JsonView
                          value={studyForms}
                          collapsed={false}
                          displayDataTypes={false}
                          style={{
                            backgroundColor: 'transparent',
                            fontSize: '12px',
                          }}
                        />
                      ) : (
                        <Text color="gray">
                          Click "Refresh" to load study forms data
                        </Text>
                      )}
                    </Box>
                  </Flex>
                </Card>
              </Tabs.Content>

              <Tabs.Content value="mapping" style={{ marginTop: '1rem' }}>
                <Card>
                  <Flex direction="column" gap="3">
                    <Flex justify="between" align="center">
                      <Text size="3" weight="bold">
                        Current Mapping Object
                      </Text>
                      <Flex gap="2">
                        <Button
                          size="small"
                          variant="soft"
                          color="green"
                          onClick={downloadMapping}
                        >
                          <IconDownload size={14} />
                          Download
                        </Button>
                        <Button
                          size="small"
                          variant="soft"
                          color="blue"
                          onClick={uploadMapping}
                        >
                          <IconUpload size={14} />
                          Upload
                        </Button>
                      </Flex>
                    </Flex>

                    <Box
                      style={{
                        background: 'var(--gray-2)',
                        padding: '1rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: '500px',
                      }}
                    >
                      <JsonView
                        value={wizardState}
                        collapsed={false}
                        displayDataTypes={false}
                        style={{
                          backgroundColor: 'transparent',
                          fontSize: '12px',
                        }}
                      />
                    </Box>
                  </Flex>
                </Card>
              </Tabs.Content>

              <Tabs.Content value="import-state" style={{ marginTop: '1rem' }}>
                <Card>
                  <Flex direction="column" gap="3">
                    <Text size="3" weight="bold">
                      Import Process State
                    </Text>

                    <Box
                      style={{
                        background: 'var(--gray-2)',
                        padding: '1rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: '500px',
                      }}
                    >
                      <JsonView
                        value={{
                          wizardState: wizardState,
                          timestamp: new Date().toISOString(),
                        }}
                        collapsed={false}
                        displayDataTypes={false}
                        style={{
                          backgroundColor: 'transparent',
                          fontSize: '12px',
                        }}
                      />
                    </Box>
                  </Flex>
                </Card>
              </Tabs.Content>
            </Tabs.Root>
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
};

export default StudyDataImportDebugPanel;
